import { Vector2, Vector3, Vector4 } from 'three';
import { degToRad, radToDeg } from 'three/src/math/MathUtils.js';
import { SettingState } from '../../../cross-team-shared/state/setting-state';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { addPoints } from '../point-utils';
import { ProjectTo2DOptions } from './abstract-projection-utils';
import { CdiCylindricalProjectionUtils } from './cdi-cylindrical-projection-utils';

export class CdiDeformedCylinderProjectionUtils extends CdiCylindricalProjectionUtils {
  override projectTo2D(
    point: Point3D,
    settingState: SettingState,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    this.updateProjectionCamera(state, settingState, options.outputName);
    this.setUpIntrinsics(settingState, state, options);

    const pixel = this.lightrayToPixelDeformedCylinder(point, state, options);

    return {
      position: pixel.toArray(),
      isInsideFrustum: !this.is2DPointBehindCamera(point, options),
    };
  }

  // eslint-disable-next-line max-lines-per-function -- single function to keep it similar to the original shader code
  private lightrayToPixelDeformedCylinder(
    point: Point3D,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    this.position.fromArray([...point, 1]);

    const vertexInCamera = this.position
      .applyMatrix4(options.modelMatrix)
      .applyMatrix4(this.projectionCamera.matrixWorldInverse);

    const x = -vertexInCamera.z;
    const y = -vertexInCamera.x;
    const z = vertexInCamera.y;

    const cutAngleLower = 20;
    const cutAngleUpper = -20;

    const mCutHeights = new Vector2(
      Math.tan(degToRad(cutAngleUpper)) * this.focalLength.y,
      Math.tan(degToRad(cutAngleLower)) * this.focalLength.y,
    );

    const altitudeAngle = radToDeg(Math.atan2(-z, Math.hypot(x, y)));

    if (altitudeAngle > cutAngleLower) {
      return this.lightRaySensorToPixelEquidistant(
        vertexInCamera,
        cutAngleLower,
        mCutHeights.y,
        this.getImageSize(state, options.outputName),
      );
    } else if (altitudeAngle < cutAngleUpper) {
      return this.lightRaySensorToPixelEquidistant(
        vertexInCamera,
        cutAngleUpper,
        mCutHeights.x,
        this.getImageSize(state, options.outputName),
      );
    } else {
      return this.lightrayToPixel(point, state, options);
    }
  }

  // eslint-disable-next-line max-lines-per-function -- single function to keep it similar to the original shader code
  private lightRaySensorToPixelEquidistant(
    vertexInCamera: Vector4,
    cutAngle: number,
    cutHeight: number,
    imageSize: Vector2,
  ) {
    const x = -vertexInCamera.z;
    const y = -vertexInCamera.x;
    const z = vertexInCamera.y;

    const azimuth = Math.atan2(-y, x);
    const altitude = Math.atan(-z / Math.hypot(x, y));

    const altitudeEquidistant =
      altitude - Math.asin(Math.tan(degToRad(cutAngle)) * Math.cos(altitude));

    const lightRayVectorEquidistant = new Vector3(
      Math.cos(altitudeEquidistant) * Math.cos(azimuth),
      Math.cos(altitudeEquidistant) * Math.sin(azimuth),
      Math.sin(altitudeEquidistant) * -1.0,
    );

    const lightRayLength = lightRayVectorEquidistant.length();

    if (isZero(lightRayLength)) {
      return new Vector2(-2.0, -2.0);
    }

    const lightRayVectorEquidistantCV = new Vector3(
      -lightRayVectorEquidistant.y,
      -lightRayVectorEquidistant.z,
      lightRayVectorEquidistant.x,
    );

    const radiusEquidistant = lightRayVectorEquidistantCV.length();
    const theta = Math.acos(lightRayVectorEquidistantCV.z / radiusEquidistant);
    const radiusCircle = Math.hypot(
      lightRayVectorEquidistantCV.x,
      lightRayVectorEquidistantCV.y,
    );

    const ratio = isZero(radiusCircle) ? 1.0 : theta / radiusCircle;

    const pixelNormalized = new Vector2(
      ratio * lightRayVectorEquidistantCV.x,
      ratio * lightRayVectorEquidistantCV.y,
    );

    const principalPointShift: Point2D = [0, cutHeight];
    const principalPointEquidistant = addPoints(
      this.principlePoint.toArray(),
      principalPointShift,
    );

    const u =
      pixelNormalized.x * this.focalLength.x + principalPointEquidistant[0];
    const squeezeFactor = this.calculateSqueezeFactorEquidistant(u);

    const v =
      pixelNormalized.y * this.focalLength.y * squeezeFactor +
      principalPointEquidistant[1];

    return new Vector2().subVectors(imageSize, new Vector2(u, v));
  }

  private calculateSqueezeFactorEquidistant(u: number) {
    const mMaxVerticalSqueeze = 0.375;
    const pixelRatio = 1.0 - u / this.principlePoint.x;
    return 1.0 - mMaxVerticalSqueeze * (pixelRatio * pixelRatio);
  }
}

function isZero(val: number) {
  return Math.abs(val) < 0.00001;
}
